import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { enGB as en, uk } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { ampli } from '../../ampli';
import { decodeSourceName } from '../../utils/decodeURI';
import { ReactComponent as CalendarIcon } from '../../assets/calendar.svg';
import { ReactComponent as FollowersIcon } from '../../assets/followers.svg';
import { ReactComponent as ExpandIcon } from '../../assets/expand.svg';
import { ReactComponent as LinkIcon } from '../../assets/link.svg';
import { ReactComponent as ActorIcon } from '../../pages/SearchPage/assets/link.svg';
import { ReactComponent as SourceIcon } from '../../pages/SearchPage/assets/person.svg';
import { platformIconsMapping, platformNamesMapping } from '../../utils/platforms';
import { contentTypesIconsMapping, contentTypesMapping } from '../../utils/contentTypes';
import { formatWithCustomGigaBillion } from '../../utils/fomatNumbers';
import styles from './ExtendedMessageView.module.scss';

const statusMapping = {
  NEW: { class: styles.new, name: 'New' },
  READ: { class: styles.approved, name: 'Approved' },
  DELETED: { class: styles.deleted, name: 'Deleted' },
};

export function MessageInfoWrapper({
  message,
  followers_cnt,
  isShare,
  currentUser,
  narrative,
  narrartiveMessage,
  setMessageModal,
}) {
  const { t } = useTranslation();
  const getUserLocale = () => {
    const lang = localStorage.getItem('i18nextLng');
    if (lang === 'ua-UK' || lang === 'ru-RU' || lang === 'ru' || lang === 'uk') {
      return uk;
    }
    return en;
  };

  return (
    <div className={styles.messageInfoWrapper}>
      <div className={styles.messageInfo}>
        {message?.status && <div
          className={`${styles.status} ${statusMapping[message.status].class}`}
        >
          {t(statusMapping[message.status].name)}
        </div>}
        {message.content_type ? (
          <div
            className={styles.contentType}
            data-tooltip-id="message-content-type"
            data-tooltip-content={t(
              contentTypesMapping[message?.content_type],
            )}
          >
            {contentTypesIconsMapping[message?.content_type]}
          </div>
        ) : (
          ''
        )}
        <div className={styles.date}>
          <span
            className={styles.anchor}
            data-tooltip-id="message-publication-date"
            data-tooltip-content={t('Publication date')}
          >
            <CalendarIcon />
          </span>
          {format(
            parseISO(message.date_publicated || message.published_at + 'Z'),
            'dd LLL yyyy HH:mm',
            { locale: getUserLocale() },
          )}
        </div>
        <div className={styles.platform}>
          <span
            className={styles.anchor}
            data-tooltip-id="message-platform"
            data-tooltip-content={
              platformNamesMapping[message.source?.source_type]
            }
          >
            {platformIconsMapping[message.source?.source_type]}
          </span>
          {followers_cnt ? (
            <span
              className={styles.anchor}
              data-tooltip-id="message-followers"
              data-tooltip-content={t('Followers')}
            >
              <FollowersIcon />
              <span>{formatWithCustomGigaBillion(followers_cnt ?? 0)}</span>
            </span>
          ) : (
            ''
          )}
        </div>
        <div className={styles.sourceActorWrapper}>
          <span className={styles.anchor}>
            <div>
              <ActorIcon />
              <span
                className={styles.cutText}
                data-tooltip-id="source-info"
                data-tooltip-content={
                  message.source
                    ? `${decodeSourceName(message.source?.name)}\n${message.source?.url ?? t('N/A')}`
                    : t('N/A')
                }
              >
                {isShare ? (
                  decodeSourceName(message.source?.name)
                ) : message.source ? (
                  <Link
                    to={`/sources/${message.source?.id}`}
                    onClick={() => {
                      ampli.track({
                        event_type: 'Open source profile',
                        event_properties: {
                          user_id: currentUser?.id,
                          workspace_id: currentUser?.workspace_id,
                          source_id: message.source.id,
                        },
                      });
                    }}
                  >
                    {decodeSourceName(message.source.name)}
                  </Link>
                ) : (
                  t('N/A')
                )}
              </span>
            </div>
            {message.actor && message.actor.id !== message.source?.id ? (
              <div>
                <SourceIcon />
                <span
                  className={styles.cutText}
                  data-tooltip-id="actor-info"
                  data-tooltip-content={`${decodeSourceName(message.actor?.name)}\n${message.actor?.url ?? t('N/A')}`}
                >
                  {isShare ? (
                    decodeSourceName(message.actor?.name)
                  ) : (
                    <Link to={`/sources/${message.actor?.id}`}>
                      {decodeSourceName(message.actor?.name)}
                    </Link>
                  )}
                </span>
              </div>
            ) : (
              ''
            )}
            <a
              href={message.url ?? message.message_url}
              className={styles.externalLink}
              target="_blank"
              rel="noopener noreferrer"
              data-tooltip-id="publication-link"
              data-tooltip-content={
                message.content_type === 'REPOST' && message.source?.source_type === 'TIKTOK'
                  ? t('Go to original post')
                  : t('Go to publication')
              }
              onClick={() => {
                ampli.track({
                  event_type: 'Open external message link',
                  event_properties: {
                    user_id: currentUser?.id,
                    workspace_id: currentUser?.workspace_id,
                  },
                });
              }}
            >
              <LinkIcon />
            </a>
          </span>
        </div>
      </div>
      {setMessageModal && <div className={'open-message-popup ' + styles.modalIcon}>
        <ExpandIcon
          onClick={() => {
            ampli.track({
              event_type: 'Open message in a pop-up',
              event_properties: {
                user_id: currentUser?.id,
                workspace_id: currentUser?.workspace_id,
                narrative_id: narrative?.id ?? null,
              },
            });
            setMessageModal({
              message: message,
              isOpen: true,
              narrartiveMessage: narrartiveMessage,
            });
          }}
        />
      </div>}
    </div>
  );
}
